import React from 'react';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Scissors, Repeat, Wand2, Lock } from 'lucide-react';

const Row = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs text-[#8A93A6]">{label}</label>
    {children}
  </div>
);
const SingleSlider = ({ value, onChange, min = 0, max = 200, step = 1, testId }) => (
  <div className="flex items-center gap-3">
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} className="flex-1" data-testid={testId} />
    <span className="text-xs text-white w-12 text-right">{Math.round(value)}</span>
  </div>
);

/**
 * Audio controls for a video- or audio-track item: volume/gain/fade,
 * detach (video items) / replace source (audio items), noise reduction +
 * voice enhancement settings, and track-level ducking when the selected
 * item sits on an audio track. Noise reduction / voice enhancement only
 * ever show a genuine "pending integration" status — no authorized
 * audio-processing service exists yet, so neither is ever marked
 * completed here. Ducking is persisted for the rendering worker only;
 * the live preview does not apply it in this batch.
 */
export function EditorAudioControls({ item, track, siblingSources, allTracks, onAudioChange, onDetachAudio, onReplaceAudioSource, onAudioProcessingChange, onDuckingChange }) {
  const audio = item.audio || { volume_percent: 100, muted: false, gain_db: 0, fade_in_seconds: 0, fade_out_seconds: 0 };
  const processing = audio.processing || { noise_reduction_enabled: false, noise_reduction_strength_percent: 50, voice_enhancement_enabled: false, status: 'not_requested' };
  const ducking = track?.ducking || { enabled: false, trigger_track_id: null, duck_level_percent: 30 };
  const fadeMax = Math.max(0.5, Math.min(10, item.duration_seconds || 10));

  return (
    <div className="space-y-4 pt-2 border-t border-white/5" data-testid="audio-controls-panel">
      <p className="text-xs text-[#555E6E] uppercase tracking-wide">Audio</p>
      <Row label="Volume (%)"><SingleSlider value={audio.volume_percent ?? 100} min={0} max={200} onChange={(v) => onAudioChange({ volume_percent: v })} testId="inspector-volume-slider" /></Row>
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#8A93A6]">Mute</span>
        <Switch checked={!!audio.muted} onCheckedChange={(v) => onAudioChange({ muted: v })} data-testid="inspector-mute-switch" />
      </div>
      <Row label="Gain (dB)"><SingleSlider value={audio.gain_db ?? 0} min={-60} max={24} onChange={(v) => onAudioChange({ gain_db: v })} testId="inspector-gain-slider" /></Row>
      <div className="grid grid-cols-2 gap-3">
        <Row label="Fade In (s)"><SingleSlider value={audio.fade_in_seconds ?? 0} min={0} max={fadeMax} step={0.1} onChange={(v) => onAudioChange({ fade_in_seconds: Math.min(v, Math.max(0, (item.duration_seconds || fadeMax) - (audio.fade_out_seconds ?? 0))) })} testId="inspector-fade-in-slider" /></Row>
        <Row label="Fade Out (s)"><SingleSlider value={audio.fade_out_seconds ?? 0} min={0} max={fadeMax} step={0.1} onChange={(v) => onAudioChange({ fade_out_seconds: Math.min(v, Math.max(0, (item.duration_seconds || fadeMax) - (audio.fade_in_seconds ?? 0))) })} testId="inspector-fade-out-slider" /></Row>
      </div>

      {item.item_type === 'video' && !item.audio_detached && (
        <Button size="sm" variant="outline" className="w-full border-white/10" onClick={onDetachAudio} data-testid="inspector-detach-audio-button">
          <Scissors className="h-3.5 w-3.5 mr-2" />Detach Audio
        </Button>
      )}
      {item.item_type === 'video' && item.audio_detached && (
        <p className="text-[10px] text-[#8A93A6]" data-testid="inspector-audio-detached-note">Audio detached — edit it on its own audio-track item.</p>
      )}

      {item.item_type === 'audio' && (
        <div className="space-y-1.5">
          <label className="text-xs text-[#8A93A6] flex items-center gap-1.5"><Repeat className="h-3 w-3" />Replace Audio Source</label>
          <Select value={item.content_source_id || ''} onValueChange={onReplaceAudioSource}>
            <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="inspector-replace-audio-select"><SelectValue placeholder="Choose a source" /></SelectTrigger>
            <SelectContent>{(siblingSources || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.title || s.id}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      <div className="pt-2 border-t border-white/5 space-y-2">
        <p className="text-xs text-[#555E6E] uppercase tracking-wide flex items-center gap-1.5"><Wand2 className="h-3 w-3" />Processing</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#8A93A6]">Noise Reduction</span>
          <Switch checked={!!processing.noise_reduction_enabled} onCheckedChange={(v) => onAudioProcessingChange({ noise_reduction_enabled: v })} data-testid="inspector-noise-reduction-switch" />
        </div>
        {processing.noise_reduction_enabled && (
          <Row label="Strength (%)"><SingleSlider value={processing.noise_reduction_strength_percent ?? 50} onChange={(v) => onAudioProcessingChange({ noise_reduction_strength_percent: v })} testId="inspector-noise-reduction-strength-slider" /></Row>
        )}
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#8A93A6]">Voice Enhancement</span>
          <Switch checked={!!processing.voice_enhancement_enabled} onCheckedChange={(v) => onAudioProcessingChange({ voice_enhancement_enabled: v })} data-testid="inspector-voice-enhancement-switch" />
        </div>
        {processing.status === 'pending_integration' && (
          <p className="flex items-center gap-1 text-[10px] text-amber-400/80" data-testid="inspector-audio-processing-status">
            <Lock className="h-3 w-3" />Pending processing integration — no authorized audio-processing service connected yet.
          </p>
        )}
      </div>

      {item.item_type === 'audio' && (
        <div className="pt-2 border-t border-white/5 space-y-2">
          <p className="text-xs text-[#555E6E] uppercase tracking-wide">Track Ducking ({track?.name})</p>
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#8A93A6]">Duck under another track</span>
            <Switch checked={!!ducking.enabled} onCheckedChange={(v) => onDuckingChange({ enabled: v })} data-testid="inspector-ducking-switch" />
          </div>
          {ducking.enabled && (
            <>
              <Row label="Duck Level (%)"><SingleSlider value={ducking.duck_level_percent ?? 30} onChange={(v) => onDuckingChange({ duck_level_percent: v })} testId="inspector-ducking-level-slider" /></Row>
              <Row label="Triggered By">
                <Select value={ducking.trigger_track_id || ''} onValueChange={(tid) => onDuckingChange({ trigger_track_id: tid })}>
                  <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="inspector-ducking-trigger-select"><SelectValue placeholder="Choose a track" /></SelectTrigger>
                  <SelectContent>{(allTracks || []).filter((t) => t.id !== track?.id).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </Row>
            </>
          )}
          <p className="text-[10px] text-[#555E6E]">Applied by the rendering worker — not previewed live in this batch.</p>
        </div>
      )}
    </div>
  );
}
